// src/components/ModulesOverview.js

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  PlayCircle,
  Lock,
  Clock,
  ArrowRight,
} from "lucide-react";

import Header from "../Header/Header";
import MovingTicker from "../Header/MovingTicker";
import a1 from "./M.png";
import { useTranslation } from "react-i18next";


{/*
    
  {
    id: "B",
    code: "B",
    title: "Income, Deductions, Credits and The Final Tax Amount",
    // or use descriptionKey if you have one in your translations
    description:
      "Your biggest breakthroughs come from mastering this flow. When you see exactly how income turns into taxable income, how deductions shrink it, and how credits lower your tax dollar-for-dollar, you unlock the ability to make smarter money moves all year — not just at tax time.",
    estMinutes: "",
    level: "",
    storiesCount: 11,
    statusKey: "inProgress",
    path: "/Explain1040",
    roleLabel: "Start here",
  },
  
  */}
const MODULES = [
  {
    id: "A",
    code: "A",
    // i18n keys
    titleKey: "pg1-card1h",
    descriptionKey: "pg1-card1hsub",
    estMinutes: "",
    level: "Most missed",
    storiesCount: 11,
    statusKey: "inProgress",
    path: "/saver",
    roleLabel: "Start here",
  },


  
];

const STATUS_META = {
  notStarted: {
    label: "Not started",
    icon: PlayCircle,
    pillClass: "bg-slate-100 text-slate-700",
  },
  inProgress: {
    label: "In progress",
    icon: PlayCircle,
    pillClass: "bg-amber-100 text-amber-800",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    pillClass: "bg-emerald-100 text-emerald-800",
  },
  locked: {
    label: "Coming soon",
    icon: Lock,
    pillClass: "bg-slate-100 text-slate-400",
  },
};

export default function ModulesOverview() {
  const { t } = useTranslation();

  const userProgress = {
    A: "inProgress",
    B: "inProgress",
    C: "locked",
  };

  const overallProgress = useMemo(() => {
    const total = MODULES.filter((m) => m.statusKey !== "locked").length;
    const completed = MODULES.filter(
      (m) => userProgress[m.code] === "completed"
    ).length;
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  }, [userProgress]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 via-orange-50 to-slate-50 text-slate-900">
      {/* Fixed ticker */}
      <div className="ticker-fixed">
        <MovingTicker>
          <>
            Millions of taxfilers miss out on benefits simply because we didn’t
            have guidance like Gildarc. This isn’t the usual tax education — for
            that you can visit
            <a
              href="https://apps.irs.gov/app/understandingTaxes/teacher/hows.jsp"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0f62fe",
                textDecoration: "underline",
                marginLeft: "6px",
              }}
            >
              the IRS Understanding Taxes site
            </a>
            . This explains the stages professionals usually charge money to
            walk people through.
          </>
        </MovingTicker>
      </div>

      <Header />

      <section className="mx-auto max-w-5xl px-6 py-16">
        {/* Page heading */}
        <header className="mb-10 text-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-1 text-sm font-medium text-amber-800 mb-3">
            {t("pg1-pathLabel", "Your Gildarc learning path")}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            {t("pg1-heading")}
          </h1>
          <p className="max-w-2xl mx-auto text-slate-600">
            {t("pg1-subheading")}
          </p>
        </header>

        {/* Module list */}
        <div className="space-y-4 mt-8">
          {MODULES.map((module) => {
            const statusKey = userProgress[module.code] || module.statusKey;
            const status = STATUS_META[statusKey];
            const StatusIcon = status.icon;
            const locked = statusKey === "locked";

            const cardBase =
              "relative rounded-2xl border border-slate-200 bg-white px-5 py-4 md:px-6 md:py-5 pl-9 md:pl-10";
            const cardStateClasses = "shadow-sm";

            const badgeBase =
              "absolute left-3 top-5 hidden sm:flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold shadow-sm";
            const badgeStateClasses = locked
              ? " bg-slate-200 text-slate-600"
              : " bg-slate-900 text-white";

            const isPrimary = module.roleLabel === "Start here";

            const moduleTitle = module.titleKey
              ? t(module.titleKey)
              : module.title;

            const moduleDescription = module.descriptionKey
              ? t(module.descriptionKey)
              : module.description;

            return (
              <article
                key={module.id}
                className={`${cardBase} ${cardStateClasses}`}
              >
                {/* Letter badge */}
                <div className={badgeBase + badgeStateClasses}>
                  {module.code}
                </div>

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  {/* Text + status */}
                  <div className="text-left flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {module.roleLabel && (
                        <span
                          className={
                            isPrimary
                              ? "inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium uppercase tracking-wide text-amber-800"
                              : "inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-700"
                          }
                        >
                          {module.roleLabel}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-0.5 text-xs text-slate-500">
                       
                        {module.estMinutes} 
                      </span>
                      <span className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-0.5 text-xs text-slate-500">
                        {module.level}
                      </span>
                    </div>

                    <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-1">
                      {moduleTitle}
                    </h2>

                    <p className="text-sm md:text-base text-slate-600 mb-2">
                      {moduleDescription}
                    </p>

                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${status.pillClass}`}
                    >
                      <StatusIcon className="h-3.5 w-3.5" />
                      {status.label}
                    </span>

                    {/* Embedded visual for Module B */}
                    {module.code === "B" && (
                      <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                        <img src={a1} alt="Example" width="600" />
                      </div>
                    )}
                  </div>

                  {/* CTA button */}
                  <div className="flex flex-col items-stretch gap-2 md:items-end md:min-w-[180px]">
                    {locked ? (
                      <button
                        type="button"
                        disabled
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-500 cursor-not-allowed"
                      >
                        Coming soon
                      </button>
                    ) : (
                      <Link
                        to={module.path}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 cursor-pointer transition"
                      >
                        {t("pg1-but")}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            );
          })}


        </div>
      </section>
    </div>
  );
}
